import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { Task } from '@/src/renderer/lib/db/models'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { CommentThread, type CommentThreadActions } from './CommentThread'

export interface TaskCommentsProps {
  task: Pick<Task, 'id' | 'status' | 'recurrence'>
  /** Der angefangene Kommentar — er gehört der Aufgabe, nicht dem Reiter (siehe `TaskDetails`). */
  draft: string
  onDraftChange: (value: string) => void
  /** Escape im Eingabefeld: die Tastatur zurück an die Liste. */
  onReleaseFocus?: () => void
  /** Für die Story „Ändern": dieser Kommentar beginnt im Feld. */
  editingId?: string
}

/**
 * **Der Reiter „Kommentare"** der Aufgaben-Details (#183, Prototyp B) — die Kommentar-Liste
 * (`CommentThread`) an einer Aufgabe.
 *
 * An einer erledigten Aufgabe genauso: Ein Nachtrag nach dem Erledigen ist üblich. Ist sie die
 * erledigte Runde einer Serie und leer, sagt ein Satz, wohin die Kommentare gegangen sind — ohne
 * Sprung dorthin: Eine Runde kennt ihren Nachfolger nicht (ADR 0021).
 *
 * @example
 * <TaskComments task={task} draft={draft} onDraftChange={setDraft} onReleaseFocus={focusList} />
 */
export const TaskComments: React.FC<TaskCommentsProps> = ({ task, ...props }) => {
  const { t } = useTranslation()
  const source = useDataSource()
  const comments = source.useTaskComments(task.id)
  const actions = useMemo<CommentThreadActions>(() => ({
    create: content => source.createComment(task.id, { content }),
    update: (id, content) => source.updateComment(id, content),
    remove: id => source.deleteComment(id),
    restore: id => source.restoreComment(id),
  }), [source, task.id])

  const completedRound = task.status === 'completed' && task.recurrence != null

  return (
    <CommentThread
      {...props}
      comments={comments}
      actions={actions}
      ownerKey={task.id}
      emptyText={completedRound ? t('taskComments.emptyCompletedRound') : t('taskComments.empty')}
    />
  )
}
